import React, { JSX } from 'react';
import MuiButton from '@mui/material/Button';
import { CommonPropsAndTypes } from '../../_Types/StandardComponentProps';
import EDTooltip from '../../Tooltip/EDTooltip';
import EDTypography from '../../Typography/EDTypography';
import { Box, styled, SxProps } from '@mui/material';
import { spacing, SpacingProps } from '@mui/system';
import { useIsExtraSmallScreen } from '../../../Hooks/useFullscreenView';

export interface IProps extends SpacingProps {
  label?: string | JSX.Element;
  shortLabel?: string;
  captionLabel?: boolean;
  labelBold?: boolean;
  labelNotUpperCase?: boolean;
  wordBreak?: boolean;
  tooltip?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  disabledReason?: React.ReactNode;
  primary?: boolean;
  flat?: boolean;
  outlined?: boolean;
  squareSize?: number;
  icon?: React.ReactNode;
  buttonRef?: React.Ref<any> | React.RefObject<any>;
  testId?: string;
  component?: any;
  children?: React.ReactNode;
  color?: CommonPropsAndTypes.ColorType;
  hoverBackgroundColor?: string;
}

//Skillnaden på alla label och title?

const getSquareStyle = (size: number): SxProps => ({
  width: `${size}px`,
  height: `${size}px`,
  minWidth: `${size}px`,
  minHeight: `${size}px`,
  padding: '8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  whiteSpace: 'normal',
  lineHeight: '1.2',
});

/**
 * Fyrkantig knapp - storlek osv kan styras genom props
 */
export const EDSquareButtonBase: React.FC<
  React.PropsWithChildren<IProps & { className?: string }>
> = props => {
  const { buttonRef } = props;
  const isExtraSmallScreen = useIsExtraSmallScreen();
  const squareSize = props.squareSize || 100;

  const color: CommonPropsAndTypes.ColorType | undefined = props.primary
    ? 'primary'
    : props.color != null
    ? props.color
    : undefined;

  const component = (
    <MuiButton
      disabled={props.disabled}
      onClick={props.onClick}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      color={color}
      variant={
        props.flat ? undefined : props.outlined ? 'outlined' : 'contained'
      }
      sx={{
        ...getSquareStyle(squareSize),
        wordBreak: props?.wordBreak === false ? 'normal' : 'break-word',
        ...(props.labelBold ? { fontWeight: 900 } : {}),
        ...(props.labelNotUpperCase ? { textTransform: 'none' } : {}),
        ...(props.hoverBackgroundColor
          ? {
              '&:hover': {
                backgroundColor: props.hoverBackgroundColor,
              },
            }
          : {}),
      }}
      ref={buttonRef}
      data-testid={props.testId}
      component={props.component}
      className={props.className}
    >
      {props.icon && (
        <Box
          sx={{
            marginBottom: 0.8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {props.icon}
        </Box>
      )}
      {!props.shortLabel && (
        <EDTypography
          variant="body2"
          color="inherit"
          style={{ textAlign: 'center' }}
          disableLetterSpacing
        >
          {props.label}
        </EDTypography>
      )}
      {props.shortLabel && (
        <EDTypography
          variant="body2"
          color="inherit"
          style={{ textAlign: 'center' }}
          disableLetterSpacing
        >
          {isExtraSmallScreen ? props.shortLabel : props.label}
        </EDTypography>
      )}
      {props.children}
    </MuiButton>
  );

  if ((props.disabled && props.disabledReason) || props.tooltip) {
    return (
      <EDTooltip
        title={props.disabled ? props.disabledReason : props.tooltip}
        enableClickOnTouch
      >
        <Box
          component={'span'}
          sx={{
            display: 'inline-block',
            overflow: 'hidden',
          }}
        >
          {component}
        </Box>
      </EDTooltip>
    );
  }

  return component;
};

const EDSquareButton: React.FC<React.PropsWithChildren<IProps>> = styled(
  EDSquareButtonBase,
  { name: 'EDSquareButton' }
)(spacing);
export default EDSquareButton;
